import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import styles from "../styles_about/HeroSection2.module.css";

export default function HeroSection() {
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  const cards = [
    {
      title: "Our Mission",
      description:
        "To simplify complex operations through reliable digital systems and automation.",
      className: "missionCard",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="4" />
          <path d="M14.8 9.2 20 4" />
          <path d="M16 4h4v4" />
        </svg>
      ),
    },
    {
      title: "Our Vision",
      description:
        "To become a trusted technology partner for governments, enterprises, and growing organizations.",
      className: "visionCard",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6S2.5 12 2.5 12Z" />
          <circle cx="12" cy="12" r="2.8" />
        </svg>
      ),
    },
    {
      title: "What We Build",
      description:
        "Government portals, WhatsApp systems, dashboards, workflow automation, and AI solutions.",
      className: "buildCard",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m12 3 9 5-9 5-9-5 9-5Z" />
          <path d="m3 13 9 5 9-5" />
          <path d="m3 17 9 5 9-5" />
        </svg>
      ),
    },
  ];

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return undefined;
    }

    const context = canvas.getContext("2d");

    if (!context) {
      return undefined;
    }

    const binaryCharacters =
      "010101100110100101110011011010000111011001100001";

    const fontSize = 14;
    let drops = [];
    let animationInterval;

    const resizeCanvas = () => {
      const devicePixelRatio = window.devicePixelRatio || 1;

      canvas.width = window.innerWidth * devicePixelRatio;
      canvas.height = canvas.parentElement.offsetHeight * devicePixelRatio;

      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${canvas.parentElement.offsetHeight}px`;

      context.setTransform(
        devicePixelRatio,
        0,
        0,
        devicePixelRatio,
        0,
        0
      );

      const numberOfColumns = Math.floor(window.innerWidth / fontSize);
      drops = Array(numberOfColumns)
        .fill(0)
        .map(() => Math.floor(Math.random() * 30));
    };

    const drawMatrix = () => {
      const canvasWidth = window.innerWidth;
      const canvasHeight = canvas.parentElement.offsetHeight;

      context.fillStyle = "rgba(0, 10, 28, 0.065)";
      context.fillRect(0, 0, canvasWidth, canvasHeight);

      context.font = `${fontSize}px monospace`;

      for (let index = 0; index < drops.length; index += 1) {
        const character =
          binaryCharacters[
            Math.floor(Math.random() * binaryCharacters.length)
          ];

        const xPosition = index * fontSize;
        const yPosition = drops[index] * fontSize;

        const brightness = Math.random();

        context.fillStyle =
          brightness > 0.92
            ? "rgba(0, 255, 220, 0.75)"
            : "rgba(0, 200, 170, 0.34)";

        context.fillText(character, xPosition, yPosition);

        if (yPosition > canvasHeight && Math.random() > 0.975) {
          drops[index] = 0;
        }

        drops[index] += 1;
      }
    };

    resizeCanvas();

    animationInterval = window.setInterval(drawMatrix, 55);
    window.addEventListener("resize", resizeCanvas);

    return () => {
      window.clearInterval(animationInterval);
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  return (
    <section className={styles.heroSection}>
      {/* Matrix background */}
      <canvas ref={canvasRef} className={styles.matrixCanvas} />

      {/* Animated light beams */}
      <div className={styles.lightBeams}>
        <div
          className={styles.beam}
          style={{ left: "26%", animationDelay: "0s" }}
        />
        <div
          className={styles.beam}
          style={{ left: "52%", animationDelay: "1.8s" }}
        />
        <div
          className={styles.beam}
          style={{ left: "77%", animationDelay: "3.4s" }}
        />
      </div>

      {/* Floating background shapes */}
      <motion.div
        className={styles.floatingShape1}
        animate={{
          y: [0, 18, 0],
          rotate: [0, 8, 0],
        }}
        transition={{
          repeat: Infinity,
          duration: 8,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className={styles.floatingShape2}
        animate={{
          y: [0, -16, 0],
          rotate: [0, -8, 0],
        }}
        transition={{
          repeat: Infinity,
          duration: 10,
          ease: "easeInOut",
        }}
      />

      <div className={styles.heroContent}>
        <motion.div
          className={styles.aboutIntroduction}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <h1 className={styles.heroTitle}>
            About <span>VishvaConnect</span>
          </h1>

          <div className={styles.titleDivider}>
            <span />
            <i />
            <span />
          </div>

          <h2 className={styles.heroSubtitle}>
            Building practical software, automation, and digital systems
            <br className={styles.desktopLineBreak} />
            that help organizations work smarter.
          </h2>

          <p className={styles.heroDescription}>
            VishvaConnect Technologies LLP is a software development company
            focused on government portals, WhatsApp automation, MIS dashboards,
            workflow automation, and AI-powered digital solutions. We build
            secure, scalable, and user-friendly systems designed for real
            operational needs.
          </p>

          <div className={styles.actionButtons}>
            <motion.button
              type="button"
              className={`${styles.actionButton} ${styles.primaryButton}`}
              whileHover={{
                scale: 1.035,
                boxShadow: "0 0 24px rgba(0, 153, 255, 0.45)",
              }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate("/services")}
            >
              <span className={styles.buttonIcon}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="7" width="18" height="13" rx="2" />
                  <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <path d="M3 12h18" />
                  <path d="M10 12v2h4v-2" />
                </svg>
              </span>

              View Services
            </motion.button>

            <motion.button
              type="button"
              className={`${styles.actionButton} ${styles.secondaryButton}`}
              whileHover={{
                scale: 1.035,
                boxShadow: "0 0 22px rgba(0, 153, 255, 0.22)",
              }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate("/contact")}
            >
              <span className={styles.buttonIcon}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="m3 7 9 7 9-7" />
                </svg>
              </span>

              Contact Us
            </motion.button>
          </div>
        </motion.div>

        <div className={styles.cardsGrid}>
          {cards.map((card, index) => (
            <motion.article
              key={card.title}
              className={`${styles.infoCard} ${styles[card.className]}`}
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.2 + index * 0.12,
              }}
              whileHover={{
                y: -5,
              }}
            >
              <div className={styles.cardIcon}>{card.icon}</div>

              <div className={styles.cardContent}>
                <h3>{card.title}</h3>
                <div className={styles.cardDivider} />
                <p>{card.description}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}